/**
 * @module app.um
 */

import * as UM from 'app/um/models/essette-api';
import { autoinject } from "aurelia-framework";
import { ComponentBase } from "app/shared/lib/essette-site";
import { INotesConfig } from "app/core/models/essette-interfaces";
import { AuthPageContext } from 'app/um/lib/auth-page-context';
import { EventAggregator, Subscription } from 'aurelia-event-aggregator';
import { AuthChangeEvent } from 'app/um/lib/events';
import { validate } from 'app/shared/elements/ess-rule';
import { NotesGrid } from 'app/core/components/notes/notes-grid';


@autoinject()
export class AuthNotesPage extends ComponentBase {

    ////****************** Fields and Properties **************************************////
    private authId: string | null = null;
    protected readonly: boolean = false;

    protected notesConfig: INotesConfig = {
        serviceRootUrl: 'um/auths',
        targetType: 'UM.Auth',
        moduleCode: 'UM',
        addTargetProperty: this.addAuthProperty.bind(this),
        excludeSystem: false
    }

    protected validateForm = validate;
    private subscriptions: Array<Subscription> = [];


    ////****************** Element References ***************************************////
    protected $notesGrid!: NotesGrid;


    ////****************** Lifecycle events (in order of execution) ******************////
    constructor(
        private readonly context: AuthPageContext,
        private readonly events: EventAggregator,
        ...rest: any[]) {

        super(...rest);
        this.logger.source = this;
    }

    protected async canActivate(params: { authId: string }): Promise<boolean> {
        if(!params.authId) { throw new Error('Missing or invalid authId parameter'); }

        return true;
    }

    protected async activate(params: { authId: string }): Promise<void> {
        if(this.authId !== params.authId) { this.authId = params.authId; }

        this.checkPermissions();

        this.subscriptions.push(
            this.events.subscribe(AuthChangeEvent, (e: AuthChangeEvent) => this.handleAuthChangeEvent(e))
        );
    }

    protected deactivate(): void {
        this.subscriptions.forEach(s => s.dispose());
        this.subscriptions = [];
    }


    ////****************** Events ****************************************************////
    protected handleAuthChangeEvent(e: AuthChangeEvent): void {
        if(e.authReloaded) {
            this.checkPermissions();
            this.reload();
        };
    }


    ////****************** Functions *************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        const canEditAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditAuth');
        const canEditClosedAuth = this.accountContext.hasPermission('UM.Auth.Duty.EditClosedAuth');
        this.readonly = this.context?.authIsReadOnly(canEditAuth, canEditClosedAuth);
    }

    public async reload(): Promise<void> {
        if(this.authId && this.$notesGrid) {
            this.$notesGrid.reload();
        }
    }

    private addAuthProperty(note: UM.AuthNote): void {
        note.auth = { id: this.authId };
    }

}

